import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Terminal, Zap } from "lucide-react";

const MODES = [
  {
    id: "privati",
    label: "Privati",
    testid: "interactive-demo-tab-privati",
    prompt: "helply.preventivo --cliente=privato",
    lines: [
      "> Sopralluogo gratuito a domicilio entro 48 ore dalla richiesta.",
      "> Preventivo chiaro e senza impegno: ogni voce spiegata, zero sorprese.",
      "> Intervento con personale qualificato, puntuale e assicurato.",
      "> Pagamento flessibile alla fine dell'intervento, senza anticipi nascosti.",
    ],
  },
  {
    id: "aziende",
    label: "Aziende e Villaggi",
    testid: "interactive-demo-tab-aziende",
    prompt: "helply.preventivo --cliente=azienda",
    lines: [
      "> Analisi degli spazi: interni, esterni e aree verdi aziendali.",
      "> Piano di pulizia e manutenzione personalizzato, anche continuativo.",
      "> Gestione dipendenti e turnazioni coordinate dal nostro team.",
      "> Reportistica periodica e referente dedicato sempre disponibile.",
    ],
  },
  {
    id: "enti",
    label: "Enti Pubblici",
    testid: "interactive-demo-tab-enti",
    prompt: "helply.preventivo --cliente=ente",
    lines: [
      "> Progettazione, pulizia e manutenzione di aree verdi pubbliche.",
      "> Pulizia e manutenzione programmata di parchi e spazi pubblici.",
      "> Convenzioni e affidamenti con massima trasparenza amministrativa.",
      "> Squadre dedicate con mezzi e attrezzature certificate.",
    ],
  },
];

export default function ConsoleDemo() {
  const [active, setActive] = useState(MODES[0]);

  return (
    <section id="console" data-testid="console-demo-section" className="relative py-24 lg:py-32">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(245,130,32,0.10), transparent 60%)",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-neon"
        >
          {"// Come Lavoriamo"}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 max-w-2xl font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl"
        >
          Dal sopralluogo all'intervento, <span className="text-outline-cyan">senza pensieri.</span>
        </motion.h2>

        <div className="mt-12 flex flex-wrap gap-3">
          {MODES.map((m) => (
            <button
              key={m.id}
              data-testid={m.testid}
              onClick={() => setActive(m)}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                active.id === m.id
                  ? "border-neon bg-neon text-white shadow-[0_10px_30px_rgba(53,101,174,0.4)]"
                  : "border-[#D9E4F5] bg-white text-slate-500 hover:border-neon/50 hover:text-neon"
              }`}
            >
              <Zap className="h-3.5 w-3.5" />
              {m.label}
            </button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 overflow-hidden rounded-2xl border border-[#16345F]/20 bg-[#0E1B33] shadow-[0_24px_80px_rgba(22,52,95,0.25)]"
          data-testid="console-terminal"
        >
          <div className="flex items-center gap-2 border-b border-white/10 bg-[#16294D] px-5 py-3">
            <span className="h-3 w-3 rounded-full bg-pulse/80" />
            <span className="h-3 w-3 rounded-full bg-neon/60" />
            <span className="h-3 w-3 rounded-full bg-volt/60" />
            <span className="ml-3 flex items-center gap-2 font-mono text-xs text-slate-400">
              <Terminal className="h-3.5 w-3.5" />
              helply-console — richiesta live
            </span>
          </div>
          <div className="min-h-[260px] overflow-x-auto p-6 font-mono text-sm leading-loose sm:p-8">
            <p className="text-[#8FC1FF]">$ {active.prompt}</p>
            <AnimatePresence mode="wait">
              <motion.div key={active.id} className="mt-3 space-y-1.5">
                {active.lines.map((line, i) => (
                  <motion.p
                    key={line}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.22 }}
                    className="text-slate-300"
                  >
                    {line}
                  </motion.p>
                ))}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15 + active.lines.length * 0.22 }}
                  className="pt-2 text-[#4ADE80]"
                >
                  {"✓ Richiesta completata — HELPLY è al tuo servizio."}
                  <span className="ml-1 inline-block h-4 w-2 animate-pulse bg-[#8FC1FF] align-middle" />
                </motion.p>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
